import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@erp/ui";

import { LogoWordmark } from "@/components/brand/logo";
import { ThemeToggle } from "@/components/theme/theme-toggle";
import { SUPPORTED_LOCALES, setLocale, type Locale } from "@/lib/i18n";

import "./auth.css";

/** Decorative 3D cube — six faces carrying the system's own artifacts. */
function LedgerCube() {
  return (
    <div className="auth-cube-scene" aria-hidden="true">
      <div className="auth-cube">
        <div className="auth-cube-face auth-cube-face--front">
          <div className="auth-cube-journal">
            <span>DR</span>
            <span>CR</span>
            <span>4,200.00</span>
            <span>—</span>
            <span>—</span>
            <span>4,200.00</span>
            <span className="sum">4,200.00</span>
            <span className="sum">4,200.00</span>
          </div>
        </div>
        <div className="auth-cube-face auth-cube-face--back">
          <div className="auth-cube-barcode" />
          <span>SKU-10452</span>
        </div>
        <div className="auth-cube-face auth-cube-face--right">
          <span className="auth-cube-accent tnum">1,250.75 SAR</span>
          <span className="tnum">١٬٢٥٠٫٧٥</span>
        </div>
        <div className="auth-cube-face auth-cube-face--left">
          <span className="auth-cube-accent">IN ↑ 240</span>
          <span>OUT ↓ 180</span>
        </div>
        <div className="auth-cube-face auth-cube-face--top">
          <span>PERIOD 2026-08</span>
          <span className="auth-cube-accent">OPEN</span>
        </div>
        <div className="auth-cube-face auth-cube-face--bottom">
          <span className="auth-cube-mark">MIZAN</span>
        </div>
      </div>
    </div>
  );
}

function LocaleSelect() {
  const { t, i18n } = useTranslation();
  const current = (i18n.language.split("-")[0] ?? "en") as Locale;
  return (
    <Select value={current} onValueChange={(value) => void setLocale(value as Locale)}>
      <SelectTrigger className="w-32" aria-label={t("locale.switchLabel")}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {SUPPORTED_LOCALES.map((locale) => (
          <SelectItem key={locale} value={locale}>
            {t(`locale.${locale}`)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * Split auth gate: a constant-identity brand panel (3D ledger cube) beside a
 * theme-following form column. Mirrors automatically under RTL; the panel
 * collapses to a compact header below lg.
 */
export function AuthLayout({ children }: { children: ReactNode }) {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-full">
      <aside className="hidden w-[44%] flex-col justify-between bg-auth-panel p-10 text-auth-panel-foreground lg:flex">
        <LogoWordmark />
        <div className="grid place-items-center">
          <LedgerCube />
        </div>
        <p className="max-w-xs text-sm leading-relaxed text-auth-panel-muted">
          {t("brand.tagline", "Orders, stock, and books — one system of record.")}
        </p>
      </aside>

      <main className="relative flex flex-1 flex-col bg-background">
        <div className="flex items-center justify-end gap-2 p-4">
          <LocaleSelect />
          <ThemeToggle />
        </div>
        <div className="flex flex-1 items-start justify-center px-4 pb-12 pt-6 sm:items-center sm:pt-0">
          <div className="w-full max-w-sm">
            <div className="mb-8 lg:hidden">
              <LogoWordmark />
            </div>
            <div className="auth-card-enter">{children}</div>
          </div>
        </div>
      </main>
    </div>
  );
}
